import { useMemo } from "react";

import { api, type ApiError } from "./client";
import type {
  ApiDevboxCacheClear,
  ApiRepoSettings,
  ApiRepoSettingsRead,
  ApiRepository,
} from "./types";
import { useResource } from "./useResource";
import { useRealtimeScope } from "../realtime/useRealtimeScope";

export type RepositoryAgentCounts = {
  readonly done: number;
  readonly needsInput: number;
  readonly pending: number;
  readonly review: number;
  readonly running: number;
};

// View model for the repositories grid, normalised from the server DTO.
export type RepositoryCard = {
  readonly agents: RepositoryAgentCounts;
  readonly branch: string;
  readonly description: string | null;
  readonly id: string;
  readonly name: string;
  readonly org: string;
  readonly status: string;
  readonly workspace: string;
};

// Postgres `count(*)` arrives as a string; coerce defensively.
const num = (value: number | string): number => {
  const n = typeof value === "number" ? value : Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : 0;
};

export const mapRepository = (repo: ApiRepository): RepositoryCard => ({
  agents: {
    done: num(repo.agents_done),
    needsInput: num(repo.agents_needs_input),
    pending: num(repo.agents_pending),
    review: num(repo.agents_review),
    running: num(repo.agents_running),
  },
  branch: repo.default_branch,
  description: repo.description,
  id: repo.public_id,
  name: repo.name,
  org: repo.org_slug,
  status: repo.status,
  workspace: repo.workspace_public_id,
});

// How a repository names itself outside its own page: the switcher, the repo
// picker, the browser tab.
export const repoLabel = (repo: RepositoryCard): string =>
  `${repo.org}/${repo.name}`;

// Event kinds that can move something a repository DTO carries, on either the
// `repo:` or the `workspace:` scope. The agent counts are a `count(*)` over the
// repo's tasks, so every task lifecycle event shifts them; `repository.updated`
// covers the repo row itself (name, status, default branch). `resync` means the
// socket gapped and we may have missed any of them. Everything else on those
// scopes — PR syncs, build statuses, review rounds, runner telemetry — leaves
// the DTO untouched and would only cost a refetch.
//
// On the workspace scope the server sends `task.updated` only for the changes
// that move a task between board columns (`realtime.md` §Event kinds), which is
// exactly the subset that can move a count here.
const REPO_DTO_EVENTS: ReadonlySet<string> = new Set([
  "repository.updated",
  "resync",
  "task.archived",
  "task.created",
  "task.updated",
]);

export type UseRepositories = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly repositories: ReadonlyArray<RepositoryCard>;
};

// GET /repositories query options (`api.md` §Repositories). Every one of these
// is applied by the server: at a large installation the grid's page is a slice
// of the workspace's repositories, so filtering or ordering the slice in the
// browser would filter and order the wrong set.
export type RepositoriesQuery = {
  /** Skip the read entirely — for a list only some states of a page need. */
  readonly enabled?: boolean;
  /** Narrow to one provider org — the grid's team filter. */
  readonly orgSlug?: string | null;
  /** `activity` (default) is attention-demand first; `name` is A–Z. */
  readonly sort?: "activity" | "name";
  /** Scope to one workspace, and keep the list live on that workspace scope. */
  readonly workspaceId?: string | null;
};

export const useRepositories = (
  options: RepositoriesQuery = {},
): UseRepositories => {
  const { enabled = true, orgSlug, sort, workspaceId } = options;
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiRepository>
  >(enabled ? "/repositories" : null, {
    org_slug: orgSlug ?? undefined,
    sort,
    workspace_id: workspaceId ?? undefined,
  });
  // The grid spans every repository in the workspace, so `workspace:` is the
  // only scope it can listen on. Without this it refreshed on navigation alone:
  // a card's counts, its summary pill and its rank in the "most active" order
  // were all as stale as the last time the page was opened.
  useRealtimeScope(
    enabled && workspaceId ? `workspace:${workspaceId}` : null,
    (event) => {
      if (REPO_DTO_EVENTS.has(event.kind)) {
        refetch();
      }
    },
  );
  const repositories = useMemo(() => (data ?? []).map(mapRepository), [data]);
  return { error, loading, refetch, repositories };
};

export type UseRepository = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly repository: RepositoryCard | undefined;
};

export const useRepository = (id: string | null): UseRepository => {
  const { data, error, loading, refetch } = useResource<ApiRepository>(
    id ? `/repositories/${id}` : null,
  );
  // The rail's Tasks counter is rendered from these counts, and the chrome that
  // holds it is mounted once by `<Layout>` — it survives every navigation
  // inside the repo. Without this the count froze at whatever it was when the
  // repo was first opened and stayed wrong for the rest of the session, however
  // many agents started, finished or asked a question in the meantime.
  useRealtimeScope(id ? `repo:${id}` : null, (event) => {
    if (REPO_DTO_EVENTS.has(event.kind)) {
      refetch();
    }
  });
  const repository = useMemo(
    () => (data ? mapRepository(data) : undefined),
    [data],
  );
  return { error, loading, refetch, repository };
};

// GET /repositories/:id/branches — the repo's known branches, populated when
// the repo is linked and refreshed by sync. Powers the base-branch selector
// (onboarding.md §Confirm — "a selector populated from the repo's real
// branches"), replacing the old hardcoded develop/main/staging list.
export type ApiRepoBranch = {
  readonly head_sha: string | null;
  readonly last_synced_at: string | null;
  readonly name: string;
  readonly protected: boolean;
};

export type UseRepoBranches = {
  readonly branches: ReadonlyArray<ApiRepoBranch>;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
};

export const useRepoBranches = (id: string | null): UseRepoBranches => {
  const { data, error, loading } = useResource<ReadonlyArray<ApiRepoBranch>>(
    id ? `/repositories/${id}/branches` : null,
  );
  // Coerce to an array defensively — the base-branch selector maps over this,
  // and a malformed/empty body must never throw.
  const branches = useMemo<ReadonlyArray<ApiRepoBranch>>(
    () => (Array.isArray(data) ? (data as ReadonlyArray<ApiRepoBranch>) : []),
    [data],
  );
  return { branches, error, loading };
};

export type UseRepoSettings = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly settings: ApiRepoSettingsRead | undefined;
};

export const useRepoSettings = (id: string | null): UseRepoSettings => {
  const { data, error, loading, refetch } = useResource<ApiRepoSettingsRead>(
    id ? `/repositories/${id}/settings` : null,
  );
  return { error, loading, refetch, settings: data };
};

export const patchRepoSettings = (
  id: string,
  patch: Partial<ApiRepoSettings>,
): Promise<unknown> => api.patch(`/repositories/${id}/settings`, patch);

// POST /repositories/:id/devbox-image/clear — forget every devbox image this
// repository has built, so the next task builds one, and remove them from the
// runners (`repositories.md` §Devbox image).
export const clearDevboxImageCache = (
  id: string,
): Promise<ApiDevboxCacheClear> =>
  api.post<ApiDevboxCacheClear>(`/repositories/${id}/devbox-image/clear`);

export const removeRepository = (id: string): Promise<unknown> =>
  api.post(`/repositories/${id}/remove`);

// GET/POST/DELETE /repositories/:id/secrets — the environment every sandbox for
// this repository is started with (repositories.md §Secrets). The value is
// write-only: the list carries names and timestamps, never the secret itself.
export type ApiRepoSecret = {
  readonly created_at: string;
  readonly last_used_at: string | null;
  readonly name: string;
  readonly updated_at: string;
};

export type UseRepoSecrets = {
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly secrets: ReadonlyArray<ApiRepoSecret>;
};

export const useRepoSecrets = (id: string | null): UseRepoSecrets => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiRepoSecret>
  >(id ? `/repositories/${id}/secrets` : null);
  // Coerce defensively: the list is mapped over, and a malformed body must
  // never throw inside the settings page.
  const secrets = useMemo<ReadonlyArray<ApiRepoSecret>>(
    () => (Array.isArray(data) ? (data as ReadonlyArray<ApiRepoSecret>) : []),
    [data],
  );
  return { error, loading, refetch, secrets };
};

export const setRepoSecret = (
  id: string,
  name: string,
  value: string,
): Promise<unknown> => api.post(`/repositories/${id}/secrets`, { name, value });

export const deleteRepoSecret = (id: string, name: string): Promise<unknown> =>
  api.del(`/repositories/${id}/secrets/${encodeURIComponent(name)}`);

/**
 * A third-party account connected to this repository (`integrations.md`).
 *
 * Never the credential: `hint` is its last four characters and is the whole of
 * what the API will say about it. `selection` means something different per
 * kind — Sentry's is a boundary the vendor enforces, Linear's is a default the
 * agent is told about — which is why the panel renders them differently rather
 * than sharing one control.
 */
export type ApiRepoIntegration = {
  readonly auth_kind: string;
  readonly hint: string | null;
  /**
   * The address the vendor posts deliveries to, or `null` for a row with no
   * inbound half. Returned in full — unlike the credential — because an admin
   * has to paste it into Linear or Sentry, and one they could only see once
   * would mean rotating it whenever anyone needed to check it
   * (`integrations.md` §The ingest URL).
   */
  readonly ingest_url: string | null;
  readonly kind: string;
  readonly last_delivery_at: string | null;
  readonly last_error: string | null;
  readonly last_used_at: string | null;
  readonly selection: Readonly<Record<string, string>> | null;
  readonly status: string;
  readonly updated_at: string;
  readonly webhook_last_error: string | null;
  /**
   * `unconfigured` until a first delivery verifies, then `active`; `failing`
   * when one did not. The panel shows it because a mismatched secret is
   * otherwise perfectly silent (`integrations.md` §Inbound status).
   */
  readonly webhook_status: string;
};

export type UseRepoIntegrations = {
  readonly error: ApiError | undefined;
  readonly integrations: ReadonlyArray<ApiRepoIntegration>;
  readonly loading: boolean;
  readonly refetch: () => void;
};

export const useRepoIntegrations = (id: string | null): UseRepoIntegrations => {
  const { data, error, loading, refetch } = useResource<
    ReadonlyArray<ApiRepoIntegration>
  >(id ? `/repositories/${id}/integrations` : null);
  // Coerced for the same reason the secrets list is: it is mapped over, and a
  // malformed body must not throw inside the settings page.
  const integrations = useMemo<ReadonlyArray<ApiRepoIntegration>>(
    () =>
      Array.isArray(data) ? (data as ReadonlyArray<ApiRepoIntegration>) : [],
    [data],
  );
  return { error, integrations, loading, refetch };
};

export type IntegrationWrite = {
  readonly selection: Readonly<Record<string, string>>;
  readonly value?: string;
  /**
   * The inbound half's signing secret. Sent only when the admin typed one, so
   * rotating a credential does not clear it — the same rule the outbound half
   * follows (`integrations.md` §Storage). Supplying one for the first time is
   * what mints the ingest URL.
   */
  readonly webhook_secret?: string;
};

/**
 * What the stored credential can see, per kind — the picker's options
 * (`api.md` §`GET …/integrations/{kind}/available`).
 *
 * Every field optional because the two kinds answer with different halves of
 * it: Sentry with organizations and their projects, Linear with teams and
 * projects. A panel that asked for all four would be asking one vendor for
 * something it does not have.
 */
export type ApiIntegrationCatalog = {
  readonly organizations?: ReadonlyArray<{
    readonly name: string;
    readonly projects: ReadonlyArray<{
      readonly name: string;
      readonly slug: string;
    }>;
    readonly slug: string;
  }>;
  readonly projects?: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
  }>;
  readonly teams?: ReadonlyArray<{
    readonly key: string;
    readonly name: string;
  }>;
};

/**
 * Ask the vendor what the stored credential can see.
 *
 * Reaches the vendor on every call and is the one endpoint here that can be
 * slow, so the panel calls it when a form opens rather than when the page does.
 */
export const fetchIntegrationCatalog = (
  id: string,
  kind: string,
): Promise<ApiIntegrationCatalog> =>
  api.get<ApiIntegrationCatalog>(
    `/repositories/${id}/integrations/${kind}/available`,
  );

export const setRepoIntegration = (
  id: string,
  kind: string,
  body: IntegrationWrite,
): Promise<unknown> =>
  api.put(`/repositories/${id}/integrations/${kind}`, body);

export const deleteRepoIntegration = (
  id: string,
  kind: string,
): Promise<unknown> => api.del(`/repositories/${id}/integrations/${kind}`);
